import {
  MenuButton as ButtonMenuButton,
  Menu,
  MenuItem,
  MenuItems,
  Portal,
  Transition,
} from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { Placement } from '@popperjs/core';
import React, {
  ComponentType,
  Fragment,
  ReactElement,
  ReactNode,
  createElement,
  useMemo,
} from 'react';
import { uid } from 'uid';
import { usePopper } from '../../hooks/usePopper';
import { classNames } from '../../utils';

interface MenuOptionBase {
  id?: string;
  type?: string;
}

export interface MenuOptionButton extends MenuOptionBase {
  type?: 'button';
  icon?: ReactElement | ComponentType<any>;
  title: string;
  destructive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

export interface MenuOptionDivider extends MenuOptionBase {
  type: 'divider';
}

export interface MenuOptionContent extends MenuOptionBase {
  type: 'content';
  child: ReactNode;
}

export type MenuOption =
  | MenuOptionButton
  | MenuOptionDivider
  | MenuOptionContent;

interface PropsBase {
  items: MenuOption[];
  disabled?: boolean;
  placement?: Placement;
}

interface PropsNormal extends PropsBase {
  buttonClassName?: string;
}

interface PropsWithCustomButton extends PropsBase {
  customButton: ReactNode | ((props: { open: boolean }) => ReactNode);
}

type Props = PropsNormal | PropsWithCustomButton;

export default function MenuButton({
  items,
  placement,
  disabled,
  ...props
}: Props) {
  const [trigger, container] = usePopper({
    placement: placement ?? 'bottom-start',
    strategy: 'fixed',

    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
    ],
  });

  const itemsWithIds = useMemo(
    () => items.map((item) => ({ ...item, id: item.id || uid(6) })),
    [items],
  );

  return (
    <Menu
      as="div"
      className="relative inline-block text-left"
      aria-disabled={disabled ? 'true' : undefined}
    >
      {({ open }) => (
        <>
          <div>
            {'customButton' in props ? (
              <ButtonMenuButton as="div" disabled={disabled} ref={trigger}>
                <span className="sr-only">Open options</span>
                {typeof props.customButton === 'function'
                  ? props.customButton({ open })
                  : props.customButton}
              </ButtonMenuButton>
            ) : (
              <ButtonMenuButton
                ref={trigger}
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className={classNames(
                  'flex items-center rounded-full p-1 text-gray-600 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100',
                  props.buttonClassName,
                )}
              >
                <span className="sr-only">Open options</span>
                <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
              </ButtonMenuButton>
            )}
          </div>

          <Portal>
            <Transition
              as={Fragment}
              enter="transition-opacity ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition-opacity ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems
                ref={container}
                className={classNames(
                  'z-50 mt-2 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
                )}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="py-1 text-start text-sm">
                  {itemsWithIds.map((item) => (
                    <_MenuOption key={item.id} option={item} />
                  ))}
                </div>
              </MenuItems>
            </Transition>
          </Portal>
        </>
      )}
    </Menu>
  );
}

const _MenuOption = ({ option }: { option: MenuOption }) => {
  switch (option.type) {
    case 'divider':
      return <div className="h-[1px] bg-gray-100"></div>;

    case 'content':
      return option.child;

    default:
      return (
        <MenuItem disabled={option.disabled}>
          {({ disabled }) => (
            <button
              disabled={disabled}
              onClick={option.onClick}
              className={classNames(
                'group',
                option.destructive
                  ? 'text-red-500 hover:bg-gray-100 hover:text-red-600 disabled:bg-transparent'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 disabled:bg-transparent',
                'flex w-full items-center whitespace-nowrap px-4 py-2',
              )}
            >
              {option.icon ? (
                <span
                  className={classNames(
                    option.destructive
                      ? 'text-red-400 group-hover:text-red-500'
                      : 'text-gray-400 group-hover:text-gray-500',
                    'me-3 flex h-5 w-5 items-center justify-start',
                  )}
                >
                  {React.isValidElement(option.icon)
                    ? option.icon
                    : createElement(option.icon as any, {
                        'aria-hidden': 'true',
                      })}
                </span>
              ) : undefined}
              {option.title}
            </button>
          )}
        </MenuItem>
      );
  }
};
